import { ImprovedNoise } from "three/examples/jsm/Addons.js";
import { clamp } from "../helpers/utils";
import { worldHeight } from './TerrainConstants';

export interface WaterOptions {
    color: number;
    opacity: number;
    waveSpeed: number;
    waveScale: number;
    lodThreshold: number;
}

export interface WorldOptions {
    waterLevel: number;
    snowLevel: number;
    water: WaterOptions;
}

type WorldOptionsInput = Omit<WorldOptions, 'water'> & { water?: Partial<WaterOptions> };

export class WorldGenerator {

    public readonly seed: number;
    public readonly options: WorldOptions;
    private readonly worldRenderRatio: number;

    private readonly perlin = new ImprovedNoise();

    constructor(seed: number, options: WorldOptionsInput) {
        this.seed = seed;
        this.options = {
            ...options,
            water: {
                color: 0x2389da,
                opacity: 1,
                waveSpeed: 0.0008,
                waveScale: 0.018,
                lodThreshold: 3,
                ...options.water,
            },
        };
        this.worldRenderRatio = 512 / 8192;
    }

    public fractalNoise2D(x: number, y: number, octNum: number, frq: number, amp: number): number {
        let gain = 1.0;
        let sum = 0.0;

        for (let i = 0; i < octNum; i++) {
            sum += this.perlin.noise(x * gain / frq, y * gain / frq, this.seed) * amp / gain;
            gain *= 2.0;
        }

        return sum;
    }

    // TODO: seed
    public fractalNoise3D(x: number, y: number, z: number, octNum: number, frq: number, amp: number): number {
        let gain = 1.0;
        let sum = 0.0;

        for (let i = 0; i < octNum; i++) {
            sum += this.perlin.noise(x * gain / frq, y * gain / frq, z * gain / frq) * amp / gain;
            gain *= 2.0;
        }

        return sum;
    }

    private getWorldHeightBase(x: number, z: number): number {
        // return clamp(1.0 - Math.sqrt(((x - 256) / 256) ** 2 + ((z - 256) / 256) ** 2), 0.0, 1.0);
        // return 0.5 + Math.sin(x * 0.01) * 0.5;
        // return 0.5 + Math.sin(x * 0.02) * Math.cos(z * 0.02) * 0.5;

        const rate1 = this.fractalNoise2D(x, z, 4, 100, 1);
        const rate2 = this.fractalNoise2D(x, z, 10, 200, 1);
        const rate3 = this.fractalNoise2D(x, z, 12, 400, 1);
        const rate4 = this.fractalNoise2D(x, z, 8, 800, 1);

        const base = Math.sqrt(Math.abs(rate1));
        const rock = 1 - Math.abs(rate2);
        const hill = Math.abs(rate4);
        const mountain = clamp((rate3 + rate4) * 0.5, 0, 1);

        const result = 0.03 + (base * 0.07) + (rock * 0.07) + (hill * 0.03) + (mountain * 0.8);
        return result;
    }

    public getWorldHeight(x: number, z: number): number {
        x *= this.worldRenderRatio;
        z *= this.worldRenderRatio;

        return this.getWorldHeightBase(x, z);
    }

    public getVegetationDensity(x: number, z: number): number {
        const scaledX = x * this.worldRenderRatio;
        const scaledZ = z * this.worldRenderRatio;
        const biome = clamp(0.5 + this.fractalNoise2D(scaledX, scaledZ, 4, 90, 1), 0, 1);
        const localVariation = 0.5 + this.fractalNoise2D(scaledX, scaledZ, 3, 22, 1);
        if (biome < 0.28) return 0.015 * localVariation;
        if (biome > 0.62) return 0.55 * (0.6 + localVariation * 0.4);
        return 0.18 * (0.65 + localVariation * 0.35);
    }

    public getTerrainSlope(x: number, z: number): number {
        const delta = 4;
        const dx = (this.getWorldHeight(x + delta, z) - this.getWorldHeight(x - delta, z)) * worldHeight / (2 * delta);
        const dz = (this.getWorldHeight(x, z + delta) - this.getWorldHeight(x, z - delta)) * worldHeight / (2 * delta);
        return Math.sqrt(dx * dx + dz * dz) / worldHeight;
    }
}

